import { Flame } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const testBurnout: QuizConfig = {
  id: "test-burnout",
  lastModified: "2026-07-18",
  title: "Test burnout: Da li si na ivici sagorevanja?",
  shortTitle: "Test burnout",
  description:
    "Besplatan test burnout - otkrij da li imaš znake sindroma sagorevanja i kako da reaguješ pre nego što postane kritično.",
  longDescription:
    "Ovaj test analizira tri ključne dimenzije burnout sindroma: emocionalnu iscrpljenost, cinizam i osećaj neefikasnosti. Kroz kratka pitanja prepoznaćemo koliko je tvoje telo i psiha iscrpljeni, da li gubiš motivaciju i smisao u onome što radiš, i koliko ti je teško da funkcionišeš. Na kraju dobijaš jasnu procenu rizika za burnout, personalizovane strategije za oporavak i konkretne znake kada je vreme za promene ili potragu za pomoći.",
  icon: Flame,
  illustration: {
    src: "/images/quizzes/test-burnout.webp",
    alt: "Iscrpljena osoba za radnim stolom dok kolege odlaze iz kancelarije.",
  },
  duration: "4-6 min",
  category: "Stres i burnout",
  focusAreasWanted: [
    "burnout",
    "stress",
    "work_issues",
    "emotional_regulation",
  ],
  featured: false,
  questions: [
    {
      type: "radio",
      question: "Pol:",
      options: ["Muški", "Ženski", "Ne želim da se izjasnim"],
      scores: [0, 0, 0],
    },
    {
      type: "radio",
      question:
        "Koliko često se osećaš emocionalno iscrpljeno posle radnog dana?",
      options: ["Nikad", "Retko", "Ponekad", "Često", "Skoro uvek"],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko ti je teško da se ujutru ustaneš i kreneš na posao?",
      options: [
        "Uglavnom mi nije teško",
        "Ponekad mi je teško",
        "Često mi je teško",
        "Veoma mi je teško",
        "Skoro nemoguće",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Koje fizičke simptome primećuješ? (označi sve što važi)",
      options: [
        "Konstantan umor koji ne nestaje ni posle odmora",
        "Probleme sa spavanjem ili loš kvalitet sna",
        "Glavobolje ili bolove u mišićima",
        "Česte prehlade ili slabiji imunitet",
        "Probleme sa probavom ili apetitom",
        "Ništa od ovoga",
      ],
      scores: [1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question: "Koliko često osećaš da tvoj rad nema smisla ili vrednost?",
      options: ["Nikad", "Retko", "Ponekad", "Često", "Skoro uvek"],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko se osećaš cinikno ili negativno prema poslu i ljudima na poslu?",
      options: [
        "Nikad se ne osećam tako",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Kako se manifestuje tvoj burnout? (označi sve što važi)",
      options: [
        "Gubim motivaciju i interesovanje za posao",
        "Osećam se otuđeno od kolega i klijenata",
        "Teško se fokusiram i donosim odluke",
        "Osećam da ne postižem koliko bih mogao",
        "Povlačim se iz društvenih aktivnosti",
        "Koristim nezdrave načine suočavanja (alkohol, prejedanje)",
        "Ništa od ovoga",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question:
        "Koliko često osećaš da nisi dovoljno produktivan/na ili efikasan/na?",
      options: ["Nikad", "Retko", "Ponekad", "Često", "Stalno"],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Koliko često razmišljaš da promeniš posao ili da potpuno napustiš karijeru?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Stalno razmišljam o tome",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "context",
      question: "Šta ti pomaže da se nosiš sa burnoutom? (označi sve što važi)",
      options: [
        "Odmor i vikendi bez posla",
        "Fizička aktivnost ili vežbanje",
        "Razgovor sa prijateljima ili porodicom",
        "Hobiji i aktivnosti van posla",
        "Meditacija ili tehnike relaksacije",
        "Ograničavanje radnog vremena",
        "Ništa mi posebno ne pomaže",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question:
        "Koliko teško ti je da održavaš balans između posla i privatnog života?",
      options: [
        "Nije mi teško",
        "Malo mi je teško",
        "Umereno teško",
        "Veoma teško",
        "Gotovo nemoguće",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question:
        "Opiši kako burnout ili stres na poslu utiče na tvoj život van radnog vremena:",
    },
  ],
  copy: {
    title: "Tvoj rizik od burnout sindroma",
    subtitle: "Razumevanje tvog nivoa sagorevanja",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Strategije za oporavak",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
